import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "react-calendar/dist/Calendar.css";
import v_logo from "../../assets/v_logo.svg";
import face from "../../assets/face.svg";
import { useDispatch, useSelector } from "react-redux";
import StepOne from "../StepOne";
import StepTwo from "../StepTwo";
import StepThree from "../StepThree";
import Loader from "../Loader";
import { fetchTimeslot } from "../../redux/actions/timeslot";
import { fetchAppointments } from "../../redux/actions/appointments";
import { fetchOrder } from "../../redux/actions/orders";
import { toast, Toaster } from "alert";
// import { sendEmail } from "../../redux/actions/emailSend";
import { clearOrder } from "../../redux/slices/orders";

const INPUT_HANDLE_KEY = "cachedInputHandle";

const initialState = {
  appointmentTime: "",
  invoiceNumber: "",
  firstName: "",
  lastName: "",
  age: "",
  city: "",
  email: "",
  phoneNumber: "",
  treatment: "",
  note: "",
  selectedCoverages: []
};

const Booking = ({ stepNo }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { data, loading } = useSelector((state) => state.timeslot);
  const { data: order, status: orderStatus } = useSelector(
    (state) => state.order
  );
  const [step, setStep] = useState(parseInt(stepNo) || 1);
  const toastShownRef = useRef(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [showPaymentFailedNotification, setShowPaymentFailedNotification] =
    useState(false);
  const [showPaymentSuccessNotification, setShowPaymentSuccessNotification] =
    useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");

  const getStoredDate = () => {
    const storedDate = localStorage.getItem("selectedDate");
    return storedDate ? new Date(storedDate) : new Date();
  };

  const getStoredInputHandle = () => {
    const stored = localStorage.getItem(INPUT_HANDLE_KEY);
    return stored ? JSON.parse(stored) : initialState;
  };

  const [inputHandle, setInputHandle] = useState(getStoredInputHandle);
  const [date, setDate] = useState(getStoredDate);

  useEffect(() => {
    localStorage.setItem(INPUT_HANDLE_KEY, JSON.stringify(inputHandle));
  }, [inputHandle]);

  useEffect(() => {
    localStorage.setItem("selectedDate", date.toISOString());
  }, [date]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "selectedDate") {
        setDate(new Date(event.newValue));
      }
      if (event.key === INPUT_HANDLE_KEY) {
        setInputHandle(JSON.parse(event.newValue));
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!data.length) {
      dispatch(fetchTimeslot());
    }
  }, [dispatch, data.length]);

  useEffect(() => {
    dispatch(fetchAppointments());
  }, [dispatch]);

  useEffect(() => {
    const orderIdEncoded = searchParams.get("oi");

    if (orderIdEncoded) {
      setIsLoadingOrder(true);
      setTimeout(() => {
        const orderId = atob(orderIdEncoded);
        dispatch(fetchOrder(orderId));
        setIsLoadingOrder(false);
        toastShownRef.current = false;
      }, 10000);
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    const handleOrderEffect = async () => {
      if (!order || toastShownRef.current || orderStatus !== "succeeded")
        return;

      toastShownRef.current = true;

      const encodedId = btoa(order.id);

      if (order.paymentStatus === "paid") {
        setShowPaymentSuccessNotification(true);

        setTimeout(() => {
          setShowPaymentSuccessNotification(false);
        }, 10000);

        navigate(`/afspraak/1?oi=${encodedId}`);
        setStep(1);
        toast.success("Payment successful!");

        localStorage.setItem("cachedInputHandle", JSON.stringify(initialState));
        setInputHandle(initialState);
        resetStep();
        dispatch(clearOrder());
        localStorage.removeItem("appointmentId");
        localStorage.removeItem("cachedInputHandle");
        localStorage.removeItem("appointmentData");
      } else {
        setPaymentStatus(order.paymentStatus);
        setShowPaymentFailedNotification(true);

        setTimeout(() => {
          setShowPaymentFailedNotification(false);
        }, 10000);

        navigate(`/afspraak/3?oi=${encodedId}`);
        setStep(3);
        toast.error(`Payment: ${order.paymentStatus}`);
      }
    };

    handleOrderEffect();
    // eslint-disable-next-line
  }, [order, orderStatus, navigate, dispatch]);

  const nextStep = () =>
    setStep((prev) => {
      const next = prev + 1;
      navigate("/afspraak/" + next);
      return next;
    });

  const prevStep = () =>
    setStep((prev) => {
      const previous = Math.max(1, prev - 1);
      navigate("/afspraak/" + previous);
      return previous;
    });

  const resetStep = () => {
    setStep(1);
    navigate("/afspraak/1");
  };

  const dismissNotification = () => {
    setShowPaymentFailedNotification(false);
  };

  const dismissSuccessNotification = () => {
    setShowPaymentSuccessNotification(false);
  };

  const PaymentSuccessNotification = () => {
    if (!showPaymentSuccessNotification) return null;

    return (
      <div className="payment-success-overlay">
        <div className="payment-success-notification">
          <div className="notification-header">
            <div className="notification-title">
              <div className="success-icon">✅</div>
              <h4 className="georgia-font fw-bold">Betaling gelukt!</h4>
            </div>
            <button
              className="close-btn"
              onClick={dismissSuccessNotification}
              aria-label="Sluiten"
            >
              <img
                src="/images/close.svg"
                alt="Sluiten"
                width="16"
                height="16"
              />
            </button>
          </div>
          <div className="notification-body">
            <p className="body_text mb-3">
              <strong>Gefeliciteerd!</strong> Je afspraak is succesvol geboekt
              en je betaling is verwerkt.
            </p>
            <p className="body_text mb-3">
              Je ontvangt binnen enkele minuten een bevestigingsmail met alle
              details van je afspraak.
            </p>
            <p className="body_text mb-3">
              <strong>Geen bevestigingsmail ontvangen?</strong>
              <br />
              Controleer je spam/ongewenste mail folder of neem contact op via:
            </p>
            <div className="contact-info">
              <p className="body_text mb-2">
                📞 <strong>+31 (0) 6 15 85 21 50</strong>
              </p>
              <p className="body_text mb-0">
                ✉️ <strong>info@queencenter.nl</strong>
              </p>
            </div>
          </div>
          <div className="notification-footer">
            <button
              className="payment_btn d-flex align-items-center justify-content-center gap-2"
              onClick={dismissSuccessNotification}
            >
              Geweldig!
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PaymentFailedNotification = () => {
    if (!showPaymentFailedNotification) return null;

    return (
      <div className="payment-failed-overlay">
        <div className="payment-failed-notification">
          <div className="notification-header">
            <div className="notification-title">
              <div className="error-icon">⚠️</div>
              <h4 className="georgia-font fw-bold">Betaling mislukt</h4>
            </div>
            <button
              className="close-btn"
              onClick={dismissNotification}
              aria-label="Sluiten"
            >
              <img
                src="/images/close.svg"
                alt="Sluiten"
                width="16"
                height="16"
              />
            </button>
          </div>
          <div className="notification-body">
            <p className="body_text mb-3">
              Je betaling is helaas niet gelukt. Status:{" "}
              <strong>{paymentStatus}</strong>
            </p>
            <p className="body_text mb-3">
              Geen zorgen! Je handtekening en gegevens zijn bewaard. Je kunt
              direct opnieuw proberen te betalen zonder alles opnieuw in te
              vullen.
            </p>
            <div className="contact-info">
              <p className="body_text mb-2">
                <strong>Hulp nodig?</strong>
              </p>
              <p className="body_text mb-2">
                📞 <strong>+31 (0) 6 15 85 21 50</strong>
              </p>
              <p className="body_text mb-0">
                ✉️ <strong>info@queencenter.nl</strong>
              </p>
            </div>
          </div>
          <div className="notification-footer">
            <button
              className="payment_btn d-flex align-items-center justify-content-center gap-2"
              onClick={dismissNotification}
            >
              Begrepen
            </button>
          </div>
        </div>
      </div>
    );
  };

  const progressWidth = `${(step / 3) * 100}%`;

  const renderStepComponent = () => {
    switch (step) {
      case 1:
        return (
          <StepOne
            nextStep={nextStep}
            prevStep={prevStep}
            date={date}
            data={data}
            dispatch={dispatch}
            setInputHandle={setInputHandle}
            progressWidth={progressWidth}
            setDate={setDate}
            step={step}
          />
        );
      case 2:
        return (
          <StepTwo
            date={date}
            nextStep={nextStep}
            prevStep={prevStep}
            inputHandle={inputHandle}
            setInputHandle={setInputHandle}
            progressWidth={progressWidth}
            step={step}
          />
        );
      case 3:
        return (
          <StepThree
            inputHandle={inputHandle}
            setInputHandle={setInputHandle}
            date={date}
            progressWidth={progressWidth}
            resetStep={resetStep}
            initialState={initialState}
          />
        );
      default:
        return null;
    }
  };

  if (orderStatus === "loading" || loading || isLoadingOrder) {
    return <Loader />;
  }

  return (
    <div className="booking_container">
      <Toaster position="bottom-right" theme="dark" duration={10000} />

      <PaymentSuccessNotification />
      <PaymentFailedNotification />

      <div className="far_left">
        <img src={face} alt="" />
        <img src={v_logo} alt="" className="brand" />
        <p className="version_text">V.0.1</p>
      </div>
      {renderStepComponent()}
    </div>
  );
};

export default Booking;
